import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getLocales } from 'expo-localization';
import { useCommunity } from '@/hooks/useCommunity';
export type WeightUnit = 'lb' | 'kg' | 'st';
export type HeightUnit = 'ft' | 'm';
export type DistanceUnit = 'km' | 'mi';

export const DEFAULT_WEIGHT_UNIT: WeightUnit = 'lb';
export const DEFAULT_HEIGHT_UNIT: HeightUnit = 'ft';

const STORAGE_KEY = '@measurement_preferences';

interface MeasurementPreferences {
  weightUnit: WeightUnit;
  distanceUnit: DistanceUnit;
  heightUnit: HeightUnit;
}

function getDeviceDefaults(): MeasurementPreferences {
  const locale = getLocales()[0];
  const system = locale?.measurementSystem;

  if (system === 'metric') {
    return { weightUnit: 'kg', distanceUnit: 'km', heightUnit: 'm' };
  }
  if (system === 'uk') {
    return { weightUnit: 'st', distanceUnit: 'mi', heightUnit: 'ft' };
  }
  if (system === 'us') {
    return { weightUnit: 'lb', distanceUnit: 'mi', heightUnit: 'ft' };
  }

  const region = locale?.regionCode?.toUpperCase();
  if (region === 'US' || region === 'LR' || region === 'MM') {
    return { weightUnit: 'lb', distanceUnit: 'mi', heightUnit: 'ft' };
  }
  if (region === 'GB') {
    return { weightUnit: 'st', distanceUnit: 'mi', heightUnit: 'ft' };
  }
  if (region === 'CA') {
    return { weightUnit: 'lb', distanceUnit: 'km', heightUnit: 'ft' };
  }

  return { weightUnit: 'kg', distanceUnit: 'km', heightUnit: 'm' };
}

export type MeasurementPreferencesContextValue = {
  weightUnit: WeightUnit;
  setWeightUnit: (unit: WeightUnit) => void;
  distanceUnit: DistanceUnit;
  setDistanceUnit: (unit: DistanceUnit) => void;
  heightUnit: HeightUnit;
  setHeightUnit: (unit: HeightUnit) => void;
  isLoaded: boolean;
};

const deviceDefaults = getDeviceDefaults();

const defaultContextValue: MeasurementPreferencesContextValue = {
  weightUnit: deviceDefaults.weightUnit,
  setWeightUnit: () => {},
  distanceUnit: deviceDefaults.distanceUnit,
  setDistanceUnit: () => {},
  heightUnit: deviceDefaults.heightUnit,
  setHeightUnit: () => {},
  isLoaded: false,
};

export const MeasurementPreferencesContext = createContext<MeasurementPreferencesContextValue>(defaultContextValue);

export const KG_TO_LBS = 1 / 0.45359237;
export const KG_TO_ST = 1 / 6.35029;
export const LBS_TO_KG = 0.45359237;
export const ST_TO_KG = 6.35029;
const KM_TO_MI = 0.621371;
const CM_TO_IN = 1 / 2.54;

function kgToLbs(kg: number): number {
  return kg * KG_TO_LBS;
}

function kgToStones(kg: number): number {
  return kg * KG_TO_ST;
}

function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const totalInches = cm * CM_TO_IN;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
}

function kmToMiles(km: number): number {
  return km * KM_TO_MI;
}

function calculateBmi(weightKg: number, heightCm: number): number {
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

export function weightFromKg(kg: number, unit: WeightUnit): number {
  if (unit === 'kg') return kg;
  if (unit === 'st') return kgToStones(kg);
  return kgToLbs(kg);
}

export function weightToKg(value: number, unit: WeightUnit): number {
  if (unit === 'kg') return value;
  if (unit === 'st') return value * ST_TO_KG;
  return value * LBS_TO_KG;
}

export const WEIGHT_MIN_KG = 30;
export const WEIGHT_MAX_KG = 500;

export function weightMinInUnit(unit: WeightUnit): number {
  return Math.ceil(weightFromKg(WEIGHT_MIN_KG, unit));
}

export function weightMaxInUnit(unit: WeightUnit): number {
  return Math.floor(weightFromKg(WEIGHT_MAX_KG, unit));
}

function formatWeightNumeric(kg: number, unit: WeightUnit, decimals?: number): string {
  const value = unit === 'kg' ? kg : unit === 'st' ? kgToStones(kg) : kgToLbs(kg);
  const defaultDecimals = unit === 'st' ? 1 : 0;
  const totalDecimals = defaultDecimals + (decimals ?? 0);

  if (totalDecimals === 0) {
    return String(Math.round(value));
  }
  if (unit === 'st' && decimals == null && value % 1 === 0) {
    return String(value);
  }
  return value.toFixed(totalDecimals);
}

function formatWeightSuffix(unit: WeightUnit): string {
  if (unit === 'kg') return 'kg';
  if (unit === 'st') return 'st';
  return 'lbs';
}

function formatWeight(kg: number, unit: WeightUnit, decimals?: number): string {
  return `${formatWeightNumeric(kg, unit, decimals)} ${formatWeightSuffix(unit)}`;
}

function formatWeightWithGoal(
  kg: number,
  goalKg: number | null | undefined,
  unit: WeightUnit,
  showGoal: boolean
): string {
  if (!showGoal || goalKg == null) {
    return formatWeight(kg, unit);
  }
  return `${formatWeightNumeric(kg, unit)}/${formatWeightNumeric(goalKg, unit)} ${formatWeightSuffix(unit)}`;
}

function formatHeight(cm: number, unit: HeightUnit): string {
  if (unit === 'm') {
    return `${(cm / 100).toFixed(2)} m`;
  }
  const { feet, inches } = cmToFeetInches(cm);
  return `${feet}'${inches}"`;
}

function formatDistance(km: number, unit: DistanceUnit): string {
  if (unit === 'km') {
    return km >= 5 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
  }
  const mi = kmToMiles(km);
  return mi >= 5 ? `${Math.round(mi)} mi` : `${mi.toFixed(1)} mi`;
}

function formatBmi(weightKg: number, heightCm: number): string {
  const bmi = calculateBmi(weightKg, heightCm);
  return String(Math.round(bmi));
}

export function MeasurementsProvider({ children }: { children: React.ReactNode }) {
  const [weightUnit, setWeightUnitState] = useState<WeightUnit>(deviceDefaults.weightUnit);
  const [distanceUnit, setDistanceUnitState] = useState<DistanceUnit>(deviceDefaults.distanceUnit);
  const [heightUnit, setHeightUnitState] = useState<HeightUnit>(deviceDefaults.heightUnit);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        if (raw) {
          const stored = JSON.parse(raw) as Partial<MeasurementPreferences>;
          const prefs: MeasurementPreferences = {
            weightUnit: stored.weightUnit ?? deviceDefaults.weightUnit,
            distanceUnit: stored.distanceUnit ?? deviceDefaults.distanceUnit,
            heightUnit: stored.heightUnit ?? deviceDefaults.heightUnit,
          };
          setWeightUnitState(prefs.weightUnit);
          setDistanceUnitState(prefs.distanceUnit);
          setHeightUnitState(prefs.heightUnit);
        }
      } catch (e) {
        console.error('Failed to load measurement preferences', e);
      } finally {
        setIsLoaded(true);
      }
    })();
  }, []);

  const persist = useCallback(async (prefs: MeasurementPreferences) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      console.error('Failed to save measurement preferences', e);
    }
  }, []);

  const setWeightUnit = useCallback(
    (unit: WeightUnit) => {
      setWeightUnitState(unit);
      setDistanceUnitState((d) => {
        setHeightUnitState((h) => {
          persist({ weightUnit: unit, distanceUnit: d, heightUnit: h });
          return h;
        });
        return d;
      });
    },
    [persist]
  );

  const setDistanceUnit = useCallback(
    (unit: DistanceUnit) => {
      setDistanceUnitState(unit);
      setWeightUnitState((w) => {
        setHeightUnitState((h) => {
          persist({ weightUnit: w, distanceUnit: unit, heightUnit: h });
          return h;
        });
        return w;
      });
    },
    [persist]
  );

  const setHeightUnit = useCallback(
    (unit: HeightUnit) => {
      setHeightUnitState(unit);
      setWeightUnitState((w) => {
        setDistanceUnitState((d) => {
          persist({ weightUnit: w, distanceUnit: d, heightUnit: unit });
          return d;
        });
        return w;
      });
    },
    [persist]
  );

  const value: MeasurementPreferencesContextValue = {
    weightUnit,
    setWeightUnit,
    distanceUnit,
    setDistanceUnit,
    heightUnit,
    setHeightUnit,
    isLoaded,
  };

  return (
    <MeasurementPreferencesContext.Provider value={value}>
      {children}
    </MeasurementPreferencesContext.Provider>
  );
}

export function useMeasurementPreferences() {
  return useContext(MeasurementPreferencesContext);
}

export function useMeasurementFormatters() {
  const { weightUnit, distanceUnit, heightUnit } = useMeasurementPreferences();
  const community = useCommunity();
  const hasAdvancedWeight = community?.hasAdvancedWeightFeatures ?? false;

  const fmtWeight = useCallback(
    (kg: number | null | undefined, goalKg?: number | null): string => {
      if (kg == null) return '';
      return formatWeightWithGoal(kg, goalKg, weightUnit, hasAdvancedWeight);
    },
    [weightUnit, hasAdvancedWeight]
  );

  const fmtWeightSimple = useCallback(
    (kg: number | null | undefined, decimals?: number): string => {
      if (kg == null) return '';
      return formatWeight(kg, weightUnit, decimals);
    },
    [weightUnit]
  );

  const fmtHeight = useCallback(
    (cm: number | null | undefined): string => {
      if (cm == null) return '';
      return formatHeight(cm, heightUnit);
    },
    [heightUnit]
  );

  const fmtDistance = useCallback(
    (km: number | null | undefined): string => {
      if (km == null) return '';
      return formatDistance(km, distanceUnit);
    },
    [distanceUnit]
  );

  const fmtBmi = useCallback(
    (weightKg: number | null | undefined, heightCm: number | null | undefined): string => {
      if (!hasAdvancedWeight || weightKg == null || heightCm == null || heightCm === 0) return '';
      return formatBmi(weightKg, heightCm);
    },
    [hasAdvancedWeight]
  );

  return useMemo(
    () => ({
      formatWeight: fmtWeight,
      formatWeightSimple: fmtWeightSimple,
      formatHeight: fmtHeight,
      formatDistance: fmtDistance,
      formatBmi: fmtBmi,
      hasAdvancedWeightFeatures: hasAdvancedWeight,
      weightUnit,
      distanceUnit,
      heightUnit,
    }),
    [fmtWeight, fmtWeightSimple, fmtHeight, fmtDistance, fmtBmi, hasAdvancedWeight, weightUnit, distanceUnit, heightUnit]
  );
}
